import { useCallback, useEffect, useRef, useState } from 'react';
import type { CustomerListResponse, CustomerProfileDto, RefillOptionsDto } from '@gc/shared';

import { api, ApiError } from '@/lib/api';

export interface CustomerFilters {
  search: string;
  includeInactive: boolean;
  withBalanceOnly: boolean;
  sort: 'name' | 'balance' | 'points' | 'recent';
}

export const EMPTY_CUSTOMER_FILTERS: CustomerFilters = {
  search: '',
  includeInactive: false,
  withBalanceOnly: false,
  sort: 'name',
};

/** Rows per page of the list — the API's own default, now asked for out loud. */
export const CUSTOMERS_PAGE_SIZE = 50;

export function customersQueryString(filters: CustomerFilters, page = 1): string {
  const params = new URLSearchParams();
  if (filters.search.trim()) params.set('search', filters.search.trim());
  if (filters.includeInactive) params.set('includeInactive', 'true');
  if (filters.withBalanceOnly) params.set('withBalanceOnly', 'true');
  params.set('sort', filters.sort);
  // Sent every time: the list used to send no page at all, so it only ever
  // saw the first 50 accounts however many the footer counted.
  params.set('page', String(page));
  params.set('pageSize', String(CUSTOMERS_PAGE_SIZE));
  return `?${params.toString()}`;
}

export function useCustomers(filters: CustomerFilters, page = 1) {
  const [data, setData] = useState<CustomerListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Keeps the latest filters available to the callback without recreating it.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const pageRef = useRef(page);
  pageRef.current = page;
  // Latest request wins. A filter change resets the page a render later, so
  // two loads can be in flight at once — and the stale one (the old page of
  // the new filter) must not land last and stay on screen.
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    const request = ++requestRef.current;
    setLoading(true);
    try {
      const response = await api.get<CustomerListResponse>(
        `/customers${customersQueryString(filtersRef.current, pageRef.current)}`,
      );
      if (request !== requestRef.current) return;
      setData(response);
      setError(null);
    } catch (caught) {
      if (request !== requestRef.current) return;
      setError(caught instanceof ApiError ? caught.message : null);
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, filters.search, filters.includeInactive, filters.withBalanceOnly, filters.sort, page]);

  return { data, loading, error, reload: load };
}

/** The full profile: balance, points and every history the §5.5 screen shows. */
export function useCustomerProfile(customerId: string | null) {
  const [data, setData] = useState<CustomerProfileDto | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!customerId) {
      setData(null);
      return;
    }
    setLoading(true);
    try {
      setData(await api.get<CustomerProfileDto>(`/customers/${customerId}`));
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, loading, reload: load };
}

/**
 * Bonus configuration for the refill preview.
 *
 * Loaded once per dialog rather than recomputed per keystroke: the venue's
 * tiers do not change while a cashier is counting notes, and the preview must
 * use the same rule the server will apply.
 */
export function useRefillOptions(enabled: boolean) {
  const [data, setData] = useState<RefillOptionsDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    void (async () => {
      try {
        const options = await api.get<RefillOptionsDto>('/customers/refill-options');
        if (!cancelled) setData(options);
      } catch (caught) {
        if (!cancelled) setError(caught instanceof ApiError ? caught.message : null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { data, error };
}
